import { useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";

// Components
import MDCLabel from "../core/MDCLabel";
import MDCButton from "../core/MDCButton";
import MDCStepper from "../core/MDCStepper";
import MDCLoader from "../core/MDCLoader";
import DocumentUpload from "../shared/DocumentUpload";
import ErrorText from "../shared/ErrorText";
import MDCTextWithDescription from "../core/MDCTextWithDescription";
import MDCCenteredDivider from "../core/MDCCenteredDivider";
import MDCBanner from "../core/MDCBanner";

// Button Icons
import RefreshIcon from "../iconComponents/RefreshIcon";

// Constants
import {
  PREFILLED_DOCUMENTS_BUTTON_LABEL,
  PREFILLED_DOCUMENTS_DESCRIPTION,
  PREFILLED_DOCUMENTS_TITLE,
  SIGNATURE_PROCESS_DOCUMENTS_BUTTON_LABEL,
  SIGNATURE_PROCESS_DOCUMENTS_EMAIL,
  SIGNATURE_PROCESS_DOCUMENTS_EMAIL_JP_DESCRIPTION,
  VOLLMACHT_FILE_UPLOAD,
  VOLLMACHT_FILE_UPLOAD_DESCRIPTION,
  VollmachtFormConstants,
  VollmachtRadioSteps,
  documentNames,
} from "../../constants/ManualRequestFormConstants";

// Material UI
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";

// Assets
import verimiImage from "../../assets/images/verimi-image.PNG";

// Actions
import { VollmachtsDatenBankDataActions } from "../../store/actions/userProfile/vollmachtsDatenBankAction";
import { pluginActions } from "../../store/actions/plugins/pluginActions";
import { signingProviderActions } from "../../store/actions/vorgange/signingProviderActions";

// Helpers
import {
  convertBase64StringToBlobURL,
  getFileNameFromHttpResponse,
  isVWAGGroup,
} from "../../helpers/utils";

// Inner Components
import SignatureSendEmailPopupJP from "../manualRequest/inner_components/SignatureSendEmailPopupJP";

// Constants
import { USER_ROLE_EXPERT } from "../../constants/userConstants";
import {
  PPOA_FILE_UPLOAD,
  PPOA_QES_TYPE,
  PPOA_HALTER_TYPE,
} from "../../constants/PPOARecordConstants";
import { RECHFORM_TYPE_SELF_EMPLOYED_PERSON } from "../../constants/VollmachtsDatenBankConstants";
import {
  QES_CURRENT_STAGE,
  QES_STAGE_STATUS,
} from "../../constants/userConstants";

// Shared Components
import ProviderSelection from "../shared/identityManagement/ProviderSelection";
import ProviderBanner from "../shared/identityManagement/ProviderBanner";
import SigningStartScreen from "../manualRequest/vollmacht/SigningStartScreen";
import { forgetSigningFlow } from "../manualRequest/vollmacht/signingFlowMemory";
import { formConstants } from "../../helpers/vorgange";
import DeleteProviderFlow from "./DeleteProviderFlow";
import { isSelfEmployedIndividualAddress } from "../../helpers/isSelfEmployedIndividualAddress";
import { SIGNING_PROVIDER } from "../../constants/SigningProviderConstants";

const useStyles = makeStyles((theme) => ({
  containerCSS: {
    padding: "1rem",
    backgroundColor: theme.palette.MDCColors.white,
  },
  vollmachtStep: {
    display: "flex",
    flexDirection: "column !important",
    marginBottom: "3rem !important",
  },
  width75: {
    width: "75%",
  },
  QRLoader: {
    marginTop: "1.5rem",
    height: "87px",
    width: "18em",
    display: "flex",
    justifyContent: "center",
  },
  verimiImage: {
    backgroundRepeat: "no-repeat",
    backgroundSize: "100%",
    backgroundImage: `url(${verimiImage})`,
    height: "300px",
    backgroundSize: "90%",
    backgroundPosition: "center",
  },
  vollmachtStep3rd: {
    textAlign: "left",
  },
  columnTitle: {
    fontSize: "1rem",
    fontWeight: "bold",
    fontFamily: theme.palette.typography.fontFamily,
    marginBottom: "1.25rem",
  },
  fieldGroup: {
    marginBottom: "1.25rem !important",
  },
  vollmachtRadioSubHeading: {
    fontSize: "0.875rem",
    marginLeft: "2rem",
    marginTop: "-0.5rem",
  },
  vollmachtSubHeading: {
    fontSize: "0.875rem",
    marginLeft: "2rem",
  },
  prefilledDocumentSection: {
    marginTop: ".5rem",
  },
  prefilledDocumentButtonWidth: {
    width: "100%",
  },
  qesPreviewSection: {
    marginTop: "-1.5rem",
  },
  textWrapCSS: { wordBreak: "break-all" },
  centerDivider: {
    display: "flex",
    alignTtems: "center",
    marginTop: "3rem",
    marginBottom: "2rem",
  },
  contentLeftAlign: {
    textAlign: "left",
  },
}));

const POARecordQES = (props) => {
  const poaId = props?.location?.state?.poaId;

  const {
    containerCSS,
    vollmachtStep,
    width75,
    QRLoader,
    verimiImage,
    vollmachtStep3rd,
    columnTitle,
    fieldGroup,
    vollmachtRadioSubHeading,
    vollmachtSubHeading,
    prefilledDocumentSection,
    prefilledDocumentButtonWidth,
    qesPreviewSection,
    centerDivider,
    contentLeftAlign,
  } = useStyles();

  const history = useHistory();

  const dispatch = useDispatch();

  const vollmachtsdatenbankPOAForQESStatus = useSelector(
    (state) => state?.vollmachtDatenBankReducer?.qesStatus
  );
  const rechtsformData = useSelector(
    (state) => state?.manualRequestFormReducer?.rechtsformData
  );
  const vollmachtsdatenbankPOADetails = useSelector(
    (state) => state?.vollmachtDatenBankReducer?.vollmachtsdatenbankPOADetails
  );

  const [blinkingValue, setBlinkingValue] = useState(null);
  const [loadingQRCode, setloadingQRCode] = useState(false);
  const [showSignatureSendEmail, setShowSignatureSendEmail] = useState(false);
  const [showPrefilledDocumentsLoader, setShowPrefilledDocumentsLoader] =
    useState(false);

  const [loading, setLoading] = useState(false);
  // const [qesValidationResult, setQesValidationResult] = useState(null);

  const [qesType, setQesType] = useState(
    vollmachtsdatenbankPOADetails?.signingProcess || ""
  );

  const qesProviders = useSelector(
    (state) => state?.pluginReducer?.qesProviders
  );

  const [isVollmachtDocumentError, setIsVollmachtDocumentError] = useState("");

  const [documentsUpload, setDocumentsUpload] = useState({
    dauervollmacht: vollmachtsdatenbankPOADetails?.dauervollmacht || "",
    sepaMandat: vollmachtsdatenbankPOADetails?.sepaMandat || "",
    signingProcess: qesType || "",
  });

  // Providers
  const [selectedProvider, setSelectedProvider] = useState({});
  useEffect(() => {
    const providerId = vollmachtsdatenbankPOAForQESStatus?.qesProvider;

    if (!providerId || !qesProviders?.length) {
      return;
    }

    const provider = qesProviders.find((p) => p.providerId === providerId);

    if (provider) {
      setSelectedProvider({
        ...provider,
        id: provider.providerId,
        key: provider.providerId,
        name: provider.displayName,
      });
    }
  }, [vollmachtsdatenbankPOAForQESStatus?.qesProvider, qesProviders]);

  const [showQESVendorCancelConfirm, setShowQESVendorCancelConfirm] =
    useState(false);

  const handleQESVendor = () => {
    setShowQESVendorCancelConfirm(true);
  };

  const handleQESVendorCancel = () => {
    setShowQESVendorCancelConfirm(false);
  };

  const clearSigningProviderState = () => {
    forgetSigningFlow(poaId);
    dispatch(signingProviderActions.resetSigningFlow());
    setSelectedProvider(null);
  };

  const handleQESVendorConfirm = () => {
    return dispatch(
      VollmachtsDatenBankDataActions.deleteQESProviders(poaId)
    ).then(() => {
      setShowQESVendorCancelConfirm(false);
      clearSigningProviderState();
      // Reset all provider-specific state for fresh start
      setBlinkingValue(null);
      setloadingQRCode(false);
      setShowSignatureSendEmail(false);
      setShowPrefilledDocumentsLoader(false);
      setSelectedProvider(null);
      // setQesValidationResult(null);
      setIsVollmachtDocumentError("");
    });
  };

  const prevPropsRef = useRef(null);

  let signingProcessTimerRef = useRef(null); // Ref to store the signing Process timer

  const getQRCodeURL = (vollmachtsdatenbankPOAQRCode) => {
    return convertBase64StringToBlobURL(
      vollmachtsdatenbankPOAQRCode,
      "image/png"
    );
  };

  useEffect(() => {
    if (qesProviders === undefined) {
      dispatch(pluginActions.getQESProviders());
    }
  }, [dispatch, qesProviders]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    if (poaId) {
      dispatch(
        VollmachtsDatenBankDataActions.getVollmachtBankPOADetails(poaId)
      ).then((resp) => {
        if (mounted) {
          setQesType(resp?.data?.signingProcess);
          setDocumentsUpload({
            dauervollmacht: resp?.data?.dauervollmacht,
            sepaMandat: resp?.data?.sepaMandat,
            signingProcess: resp?.data?.signingProcess,
          });
          setLoading(false);
        }
      });
    }

    return () => {
      mounted = false;
    };
  }, [poaId]);

  const vollmachtsdatenbankPOAForQESUpdate = useSelector(
    (state) =>
      state?.vollmachtDatenBankReducer?.vollmachtsdatenbankPOAForQESUpdate
  );

  const userDetails = useSelector((state) => state?.authentication?.user);
  const isExpertUser = userDetails?.roles?.includes(USER_ROLE_EXPERT);
  const updateErrorMessage =
    vollmachtsdatenbankPOAForQESUpdate?.validationMessages ||
    vollmachtsdatenbankPOADetails?.validationMessages;

  const setBlinkingIconOrRedirectToVollmachtBankList = (
    currentStage,
    stageStatus
  ) => {
    if (
      currentStage === QES_CURRENT_STAGE.IDENT &&
      stageStatus === QES_STAGE_STATUS.STARTED
    ) {
      setBlinkingValue(1);
    } else if (
      (currentStage === QES_CURRENT_STAGE.IDENT &&
        stageStatus === QES_STAGE_STATUS.COMPLETED) ||
      (currentStage === QES_CURRENT_STAGE.SIGN &&
        stageStatus === QES_STAGE_STATUS.STARTED) ||
      currentStage === QES_CURRENT_STAGE.VERIFY
    ) {
      setBlinkingValue(2);
    } else if (
      currentStage === QES_CURRENT_STAGE.SIGN &&
      stageStatus === QES_STAGE_STATUS.COMPLETED
    ) {
      clearInterval(signingProcessTimerRef?.current);
      setBlinkingValue(3);
      history.push("/vollmachtsdatenbank");
    }
  };

  useEffect(() => {
    const isNonQESProcess = qesType === PPOA_QES_TYPE;
    //in case of expert user and if QR code not generated i.e currentStage is empty, restrict the below API call
    // const isExpertAndNotCompleteStage =
    //   isExpertUser && vollmachtsdatenbankPOAForQESStatus?.currentStage === "";
    // if (
    //   isNonQESProcess &&
    //   (vollmachtsdatenbankPOAQRCode === undefined ||
    //     vollmachtsdatenbankPOAQRCode === null) &&
    //   !isExpertAndNotCompleteStage
    // ) {
    //   setloadingQRCode(true);
    //   dispatch(
    //     VollmachtsDatenBankDataActions.getVollmachtBankPOAQRCode(poaId)
    //   ).then(() => setloadingQRCode(false));
    // }

    prevPropsRef.current =
      vollmachtsdatenbankPOAForQESStatus !== undefined &&
      vollmachtsdatenbankPOAForQESStatus.currentStage;

    if (
      prevPropsRef?.current === undefined ||
      ((prevPropsRef?.current?.currentStage ?? null) !==
        vollmachtsdatenbankPOAForQESStatus?.currentStage &&
        blinkingValue !== 3)
    ) {
      const currentStage = vollmachtsdatenbankPOAForQESStatus?.currentStage;
      const stageStatus = vollmachtsdatenbankPOAForQESStatus?.stageStatus;

      if (
        prevPropsRef?.current?.currentStage !==
        vollmachtsdatenbankPOAForQESStatus?.currentStage
      ) {
        setBlinkingIconOrRedirectToVollmachtBankList(currentStage, stageStatus);
      } else if (
        prevPropsRef?.current?.currentStage ===
        vollmachtsdatenbankPOAForQESStatus?.currentStage
      ) {
        setBlinkingIconOrRedirectToVollmachtBankList(currentStage, stageStatus);
      }

      if (isNonQESProcess) {
        signingProcessTimerRef.current = setInterval(() => {
          dispatch(
            VollmachtsDatenBankDataActions.getVollmachtBankSigningStatus(poaId)
          );
        }, 10000);

        return () => {
          clearInterval(signingProcessTimerRef?.current);
        };
      }
    }

    return () => {
      if (qesType === PPOA_FILE_UPLOAD && signingProcessTimerRef?.current) {
        clearInterval(signingProcessTimerRef?.current);
      }
    };
  }, [vollmachtsdatenbankPOAForQESStatus, qesType, dispatch]);

  const checkForFileUploadError = () => {
    //If Any/All of the 2 documents contains the error then it will be displayed in Error text component under the document upload section
    let fileError = "";
    let dataArray = Object.keys(documentNames);

    for (let i = 0; i < dataArray.length; i++) {
      if (documentsUpload[dataArray[i]]?.error) {
        fileError = documentsUpload[dataArray[i]]?.error;
        break;
      }
    }
    return fileError;
  };
  const handleDocumentUpload = (event) => {
    const documentValue = { ...documentsUpload };
    documentValue[event.target.name] = event.target.value;
    setDocumentsUpload(documentValue);
  };

  const handlePOAQES = () => {
    const documentValues = {
      ...documentsUpload,
      signingProcess: PPOA_QES_TYPE,
    };

    dispatch(
      VollmachtsDatenBankDataActions.updateVollmachtBankPOAForQES(
        documentValues,
        poaId
      )
    ).then((resp) => {
      if (resp?.data) {
        dispatch(
          VollmachtsDatenBankDataActions.getVollmachtBankPOADetails(poaId)
        );
      }
    });
  };

  const checkIfNotNullOrEmpty = (value) => {
    return value !== null && value !== "";
  };

  const handleQESTypeChange = (event) => {
    const value = event.target.value;

    if (
      value === PPOA_QES_TYPE &&
      (checkIfNotNullOrEmpty(documentsUpload?.sepaMandat) ||
        checkIfNotNullOrEmpty(documentsUpload?.dauervollmacht))
    ) {
      setIsVollmachtDocumentError(
        "Bitte löschen Sie zunächst die hochgeladenen Dokumente"
      );
    } else {
      setIsVollmachtDocumentError("");

      setQesType(value);
      if (value === PPOA_QES_TYPE) {
        handlePOAQES();
      }
    }
  };

  const downloadPrefilledDocuments = () => {
    setShowPrefilledDocumentsLoader(true);
    dispatch(
      VollmachtsDatenBankDataActions.getPrefilledPPOADocumentsDownload(poaId)
    ).then((resp) => {
      setShowPrefilledDocumentsLoader(false);
      const fileName = getFileNameFromHttpResponse(resp);
      const url = window.URL.createObjectURL(new Blob([resp?.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fileName}`);
      document.body.appendChild(link);
      link.click();
    });
  };

  const QESProcessView = (
    <div className={qesPreviewSection}>
      {/* Blinker stepper */}
      <Grid marginBottom={"1rem"} width={"100%"}>
        <MDCStepper step={blinkingValue} />
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={4} className={vollmachtStep} id="step-1-container">
          <>
            <Grid marginBottom="2.5rem">
              <MDCLabel label="Scan" variant="h6" />
              <p className={width75}>
                {VollmachtFormConstants.STEP_ONE_CONTENT}
              </p>
            </Grid>
            <div>
              <MDCButton
                variant="outlined"
                label={"Neuen QR Code generieren"}
                disabled={isExpertUser}
                onClick={() => {
                  setloadingQRCode(true);
                  dispatch(
                    VollmachtsDatenBankDataActions.regenerateVollmachtBankPOAQRCode(
                      poaId
                    )
                  ).then(() => {
                    setloadingQRCode(false);
                  });
                }}
                endIcon={<RefreshIcon />}
              />
            </div>
            <div>
              {loadingQRCode ? (
                <div className={QRLoader}>
                  <MDCLoader />
                </div>
              ) : (
                <>
                  {!loadingQRCode &&
                    vollmachtsdatenbankPOAForQESStatus !== undefined &&
                    vollmachtsdatenbankPOAForQESStatus?.qrCodeImage && (
                      <img
                        alt="Vollmacht QR Code"
                        src={getQRCodeURL(
                          vollmachtsdatenbankPOAForQESStatus?.qrCodeImage
                        )}
                        width="50%"
                      />
                    )}
                </>
              )}
            </div>
          </>
        </Grid>

        <Grid item xs={4} className={vollmachtStep} id="step-2-container">
          <>
            <Grid textAlign="center" marginBottom="1.5rem">
              <MDCLabel label="Vollmachterstellung via Verimi" variant="h6" />
              <p>{VollmachtFormConstants.STEP_TWO_CONTENT}</p>
            </Grid>
            <div className={verimiImage} />
          </>
        </Grid>

        <Grid
          item
          xs={4}
          className={`${vollmachtStep} ${vollmachtStep3rd}`}
          id="step-3-container"
        >
          <Grid width="18rem" marginBottom="2.5rem">
            <MDCLabel label="Vollmacht empfangen" variant="h6" />
            <p className={contentLeftAlign}>
              {VollmachtFormConstants.POA_STEP_THREE_CONTENT}
            </p>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );

  const documentUploadView = (
    <Grid container spacing="2">
      <Grid item xs={6} padding={"2rem"}>
        <>
          <div className={prefilledDocumentSection}>
            <MDCTextWithDescription
              label={PREFILLED_DOCUMENTS_TITLE}
              description={PREFILLED_DOCUMENTS_DESCRIPTION}
            >
              <MDCButton
                className={prefilledDocumentButtonWidth}
                variant="outlined"
                label={PREFILLED_DOCUMENTS_BUTTON_LABEL}
                disabled={showPrefilledDocumentsLoader || isExpertUser}
                onClick={() => downloadPrefilledDocuments()}
              />
            </MDCTextWithDescription>
          </div>
          {!isVWAGGroup() && (
            <>
              <div className={centerDivider}>
                <MDCCenteredDivider label="Oder" />
              </div>

              <MDCTextWithDescription
                label={SIGNATURE_PROCESS_DOCUMENTS_EMAIL}
                description={SIGNATURE_PROCESS_DOCUMENTS_EMAIL_JP_DESCRIPTION}
              >
                <MDCButton
                  className={prefilledDocumentButtonWidth}
                  variant="outlined"
                  label={SIGNATURE_PROCESS_DOCUMENTS_BUTTON_LABEL}
                  onClick={() => setShowSignatureSendEmail(true)}
                  disabled={isExpertUser}
                />
              </MDCTextWithDescription>
            </>
          )}

          {showSignatureSendEmail && (
            <SignatureSendEmailPopupJP
              show={showSignatureSendEmail}
              handlePopupClose={() => setShowSignatureSendEmail(false)}
              requestId={poaId}
              vollmatchtType={"PPOA"}
              halterType={PPOA_HALTER_TYPE} // PPOA will always be for juristische Person, so passing the constant value here
            />
          )}
        </>
      </Grid>

      <Grid item xs={6} padding={"1rem"}>
        <div className={prefilledDocumentSection}>
          <MDCTextWithDescription
            label={VOLLMACHT_FILE_UPLOAD}
            description={VOLLMACHT_FILE_UPLOAD_DESCRIPTION}
          >
            {Object.keys(documentNames).map((docName, index) => {
              return index === 1 &&
                (vollmachtsdatenbankPOADetails?.zollGkVorhanden ||
                  vollmachtsdatenbankPOADetails?.steuerbefreiung ===
                    "") ? null : (
                <DocumentUpload
                  key={index}
                  documentName={docName}
                  uploadedFileData={documentsUpload[docName]}
                  handleChange={(e) => handleDocumentUpload(e)}
                  error={updateErrorMessage && updateErrorMessage[docName]}
                  isShowUploadedLabel
                />
              );
            })}
            <ErrorText
              id="file-upload-error"
              error={checkForFileUploadError()}
            />
          </MDCTextWithDescription>
        </div>
      </Grid>
    </Grid>
  );

  const useNPYouTrustFlow =
    // (vollmachtsdatenbankPOAForQESStatus?.qesProvider === "YOUTRUST" ||
    //   selectedProvider?.providerId === "YOUSIGN" ||
    //   selectedProvider === "YOUTRUST") &&
    isSelfEmployedIndividualAddress(
      vollmachtsdatenbankPOADetails,
      rechtsformData,
      true
    );

  return (
    <Grid container className={containerCSS}>
      {/* Body */}
      {loading ? (
        <Grid container margin={"10rem"} justifyContent={"center"}>
          <MDCLoader />
        </Grid>
      ) : (
        <>
          {vollmachtsdatenbankPOAForQESStatus?.errorMessage?.length > 0 &&
            (vollmachtsdatenbankPOAForQESStatus?.qesProvider ===
              SIGNING_PROVIDER.VERIMI ||
              selectedProvider?.providerId === SIGNING_PROVIDER.VERIMI ||
              selectedProvider?.key === SIGNING_PROVIDER.VERIMI) && (
              <Grid container marginBottom="1.5rem">
                <MDCBanner
                  message={vollmachtsdatenbankPOAForQESStatus.errorMessage}
                  error
                />
              </Grid>
            )}
          <Grid container spacing={2}>
            <Grid item xs={4} className={fieldGroup}>
              <Grid marginY={".5rem"}>
                <label className={columnTitle}>
                  {VollmachtRadioSteps.QES_SECTION_TITLE}
                </label>

                <FormControl component="fieldset">
                  <RadioGroup
                    aria-label="signingProcess"
                    name="signingProcess"
                    value={qesType}
                    onChange={(e) => handleQESTypeChange(e)}
                  >
                    <FormControlLabel
                      value={PPOA_QES_TYPE}
                      control={<Radio />}
                      label={VollmachtRadioSteps.QES_STEP_1_TITLE}
                      disabled={
                        props?.readOnly ||
                        isExpertUser ||
                        Boolean(
                          vollmachtsdatenbankPOAForQESStatus?.currentStage
                        )
                      }
                    />

                    <p className={vollmachtRadioSubHeading}>
                      {VollmachtRadioSteps.QES_STEP_1_SUB_TITLE}
                    </p>

                    <FormControlLabel
                      value={PPOA_FILE_UPLOAD}
                      control={<Radio />}
                      className={"defaultRadioButtonStyles"}
                      label={VollmachtRadioSteps.QES_STEP_2_TITLE}
                      disabled={
                        props?.readOnly ||
                        isExpertUser ||
                        Boolean(
                          vollmachtsdatenbankPOAForQESStatus?.currentStage
                        )
                      }
                    />

                    <p className={vollmachtSubHeading}>
                      {VollmachtRadioSteps.QES_STEP_2_SUB_TITLE}
                    </p>
                  </RadioGroup>

                  <ErrorText error={isVollmachtDocumentError} />
                </FormControl>
              </Grid>
            </Grid>
            <Grid item xs={8} className={fieldGroup} minWidth={0}>
              {qesType === PPOA_QES_TYPE ? (
                !selectedProvider?.providerId &&
                !vollmachtsdatenbankPOAForQESStatus?.qesProvider ? (
                  <ProviderSelection
                    onStartSigning={setSelectedProvider}
                    disabled={isExpertUser}
                    isPPOA
                  />
                ) : vollmachtsdatenbankPOAForQESStatus?.qesProvider ===
                    SIGNING_PROVIDER.VERIMI ||
                  selectedProvider?.providerId === SIGNING_PROVIDER.VERIMI ? (
                  <>
                    <ProviderBanner
                      provider={selectedProvider}
                      handleQESVendor={handleQESVendor}
                    />
                    <Box sx={{ marginTop: "4rem" }}>{QESProcessView}</Box>
                  </>
                ) : selectedProvider?.providerId !== SIGNING_PROVIDER.VERIMI ? (
                  <>
                    <ProviderBanner
                      provider={selectedProvider}
                      handleQESVendor={handleQESVendor}
                    />
                    <SigningStartScreen
                      provider={{
                        ...selectedProvider,
                        id: selectedProvider?.key,
                        name: selectedProvider?.displayName,
                      }}
                      personenform={formConstants.companyPerson}
                      firstName={vollmachtsdatenbankPOADetails?.vorname || ""}
                      lastName={vollmachtsdatenbankPOADetails?.nachname || ""}
                      companyName={
                        vollmachtsdatenbankPOADetails?.halterName ||
                        vollmachtsdatenbankPOADetails?.unternehmen ||
                        ""
                      }
                      holderType={
                        vollmachtsdatenbankPOADetails?.halterType || ""
                      }
                      companyAddress={{
                        street: vollmachtsdatenbankPOADetails?.strasse || "",
                        houseNumber:
                          vollmachtsdatenbankPOADetails?.houseNummer || "",
                        postalCode:
                          vollmachtsdatenbankPOADetails?.postleitzahl || "",
                        city: vollmachtsdatenbankPOADetails?.stadt || "",
                      }}
                      requestId={poaId}
                      onBackToProviders={clearSigningProviderState}
                      onCancelSigning={() => {
                        return dispatch(
                          VollmachtsDatenBankDataActions.deleteQESForPOARecords(
                            poaId
                          )
                        )
                          .then((response) => {
                            if (!response) {
                              throw new Error("QES cancellation failed");
                            }
                            return response;
                          })
                          .catch((err) => {
                            throw err;
                          });
                      }}
                      onDownloadPrefilledDocuments={downloadPrefilledDocuments}
                      documentsAvailable
                      addressDocumentAvailable={Boolean(
                        vollmachtsdatenbankPOADetails?.addressDocument
                      )}
                      isPPOA
                      ppoaSigningStatus={vollmachtsdatenbankPOAForQESStatus}
                      onGetPPOASigningStatus={() => {
                        return dispatch(
                          VollmachtsDatenBankDataActions.getVollmachtBankSigningStatus(
                            poaId
                          )
                        );
                      }}
                      isExceptionalJP={useNPYouTrustFlow}
                    />
                  </>
                ) : (
                  <></>
                )
              ) : (
                documentUploadView
              )}
            </Grid>
          </Grid>

          {showQESVendorCancelConfirm && (
            <DeleteProviderFlow
              showQESVendorCancelConfirm={showQESVendorCancelConfirm}
              handleHideQESVendorCancelConfirm={handleQESVendorCancel}
              handleQESVendorConfirm={handleQESVendorConfirm}
            />
          )}
        </>
      )}

      <Grid container mt="2.5rem" justifyContent="space-between">
        <Grid item>
          <MDCButton
            onClick={() => history.push("/vollmachtsdatenbank")}
            variant="outlined"
            label="Schließen"
          />
        </Grid>
        <Grid item>
          {qesType !== PPOA_QES_TYPE && (
            <MDCButton
              onClick={() => {
                setLoading(true);
                dispatch(
                  VollmachtsDatenBankDataActions.updateVollmachtBankPOAForQES(
                    { ...documentsUpload, signingProcess: qesType },
                    poaId
                  )
                ).then((resp) => {
                  if (resp?.data) {
                    dispatch(
                      VollmachtsDatenBankDataActions.getVollmachtBankPOADetails(
                        poaId
                      )
                    );
                    setLoading(false);
                  }
                });
              }}
              variant="contained"
              label={"In Datenbank speichern"}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default POARecordQES;
